import type {
  DateFieldProps,
  DateInputProps as DateInputPropsRac,
  DateSegmentProps,
  DateValue as DateValueRac,
  TimeFieldProps,
  TimeValue as TimeValueRac,
} from 'react-aria-components';
import {
  DateField as DateFieldRac,
  DateInput as DateInputRac,
  DateSegment as DateSegmentRac,
  TimeField as TimeFieldRac,
  composeRenderProps,
} from 'react-aria-components';

import { cn } from './helpers';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

function DateField<T extends DateValueRac>({
  className,
  children,
  ...props
}: DateFieldProps<T>) {
  return (
    <DateFieldRac
      className={composeRenderProps(className, (className) => cn(className))}
      {...props}
    >
      {children}
    </DateFieldRac>
  );
}

function TimeField<T extends TimeValueRac>({
  className,
  children,
  ...props
}: TimeFieldProps<T>) {
  return (
    <TimeFieldRac
      className={composeRenderProps(className, (className) => cn(className))}
      {...props}
    >
      {children}
    </TimeFieldRac>
  );
}

function DateSegment({ className, ...props }: DateSegmentProps) {
  return (
    <DateSegmentRac
      className={composeRenderProps(className, (className) =>
        cn(
          'text-gray-800 data-focused:bg-gray-100 data-invalid:data-focused:bg-destructive data-focused:data-placeholder:text-gray-800 data-focused:text-gray-800 data-invalid:data-placeholder:text-destructive data-invalid:text-destructive data-placeholder:text-muted-foreground/70 data-[type=literal]:text-muted-foreground/70 inline rounded p-0.5 caret-transparent outline-hidden data-disabled:cursor-not-allowed data-disabled:opacity-50 data-invalid:data-focused:text-white data-invalid:data-focused:data-placeholder:text-white data-[type=literal]:px-0',
          className
        )
      )}
      {...props}
      data-invalid
    />
  );
}

const dateInputVariants = cva(
  'relative inline-flex h-9 w-full items-center overflow-hidden whitespace-nowrap rounded-md border border-pale-border px-3 py-2 text-sm transition-[color,box-shadow] outline-none data-focus-within:border-active-border data-focus-within:ring-ring/20 data-focus-within:ring-[3px] data-focus-within:has-aria-invalid:ring-destructive/20 dark:data-focus-within:has-aria-invalid:ring-destructive/40 data-focus-within:has-aria-invalid:border-destructive group-has-disabled:cursor-not-allowed group-has-disabled:opacity-50',

  {
    variants: {
      variant: {
        outline: 'border-gray-50 hover:border-primary-300',
        fill: 'border-pale-border bg-[#FAF9FC] focus-within:hover:border-active-border hover:border-primary-300',
      },
      size: {
        xs: 'h-[32px]',
        sm: 'h-[40px]',
        md: 'h-[44px]',
        lg: 'h-[48px]',
      },
    },
    defaultVariants: {
      variant: 'fill',
      size: 'lg',
    },
  }
);

interface DateInputProps
  extends DateInputPropsRac,
    VariantProps<typeof dateInputVariants> {
  className?: string;
  unstyled?: boolean;
}

function DateInput({
  className,
  variant,
  size,
  unstyled = false,
  ...props
}: Omit<DateInputProps, 'children'>) {
  return (
    <DateInputRac
      className={composeRenderProps(className, (className) =>
        cn(!unstyled && dateInputVariants({ variant, size }), className)
      )}
      {...props}
    >
      {(segment) => <DateSegment segment={segment} />}
    </DateInputRac>
  );
}

export { DateField, DateInput, DateSegment, TimeField };
export type { DateInputProps };
